import React, { useState } from "react";
import { BRAND_ACCENT, labelStyle, inputStyle } from "../utils/constants";
import { getErrorMessage } from "../utils/formatting";

const modalOverlay: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: "16px",
};

/* ─── Seed Phrase Modal ─────────────────────────────────────────────── */

interface SeedPhraseModalProps {
    show: boolean;
    seed: string;
    onClose: () => void;
}

export const SeedPhraseModal: React.FC<SeedPhraseModalProps> = ({
    show,
    seed,
    onClose,
}) => {
    const [seedSaved, setSeedSaved] = useState(false);
    const [seedCopied, setSeedCopied] = useState(false);

    if (!show) return null;

    return (
        <div style={modalOverlay}>
            <div
                className="glass-card"
                style={{
                    width: "100%",
                    maxWidth: "520px",
                    padding: "24px",
                }}
            >
                <div
                    style={{
                        textAlign: "center",
                        marginBottom: "20px",
                    }}
                >
                    <div
                        style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            background: "rgba(239, 68, 68, 0.2)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "12px",
                        }}
                    >
                        <svg
                            style={{
                                width: "24px",
                                height: "24px",
                                color: "#ef4444",
                            }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                    <h2
                        style={{
                            fontSize: "20px",
                            fontWeight: "bold",
                            color: "white",
                            marginBottom: "8px",
                        }}
                    >
                        Save Your Seed Phrase
                    </h2>
                    <p
                        style={{
                            color: "#ef4444",
                            fontSize: "14px",
                            fontWeight: 600,
                        }}
                    >
                        This is the ONLY time your seed phrase will be shown.
                        Save it securely now. It cannot be recovered later.
                    </p>
                </div>

                <textarea
                    readOnly
                    value={seed}
                    style={{
                        width: "100%",
                        padding: "16px",
                        borderRadius: "8px",
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        color: "white",
                        fontSize: "14px",
                        lineHeight: "1.6",
                        fontFamily: "monospace",
                        resize: "none",
                        minHeight: "80px",
                        marginBottom: "12px",
                    }}
                />

                <button
                    onClick={() => {
                        navigator.clipboard.writeText(seed);
                        setSeedCopied(true);
                        setTimeout(() => setSeedCopied(false), 2000);
                    }}
                    style={{
                        width: "100%",
                        padding: "10px",
                        borderRadius: "8px",
                        background: seedCopied
                            ? `rgba(20, 241, 149, 0.15)`
                            : "rgba(255,255,255,0.1)",
                        border: seedCopied
                            ? `1px solid rgba(20, 241, 149, 0.3)`
                            : "1px solid rgba(255,255,255,0.2)",
                        color: seedCopied ? BRAND_ACCENT : "white",
                        cursor: "pointer",
                        fontSize: "13px",
                        marginBottom: "16px",
                    }}
                >
                    {seedCopied ? "Copied to clipboard" : "Copy Seed Phrase"}
                </button>

                <label
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        marginBottom: "16px",
                        fontSize: "14px",
                        color: "#d1d5db",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={seedSaved}
                        onChange={(e) => setSeedSaved(e.target.checked)}
                        style={{ width: "18px", height: "18px" }}
                    />
                    I have saved my seed phrase securely
                </label>

                <button
                    className="btn-primary"
                    onClick={() => {
                        onClose();
                    }}
                    disabled={!seedSaved}
                    style={{
                        width: "100%",
                        padding: "12px",
                        fontSize: "14px",
                        opacity: seedSaved ? 1 : 0.4,
                        cursor: seedSaved ? "pointer" : "not-allowed",
                    }}
                >
                    Close
                </button>
            </div>
        </div>
    );
};

/* ─── Delete Confirm Modal ──────────────────────────────────────────── */

interface DeleteConfirmModalProps {
    accountId: string | null;
    onClose: () => void;
    onDelete: (id: string, password: string) => Promise<void>;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
    accountId,
    onClose,
    onDelete,
}) => {
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    if (!accountId) return null;

    const handleDelete = async () => {
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await onDelete(accountId, deletePassword);
            setDeletePassword("");
            setDeleteError(null);
        } catch (err: unknown) {
            setDeleteError(getErrorMessage(err, "Failed to delete account"));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleClose = () => {
        setDeletePassword("");
        setDeleteError(null);
        onClose();
    };

    return (
        <div
            style={modalOverlay}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    handleClose();
                }
            }}
        >
            <div
                className="glass-card"
                style={{
                    width: "100%",
                    maxWidth: "480px",
                    padding: "24px",
                }}
            >
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    <div
                        style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            background: "rgba(239, 68, 68, 0.2)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "12px",
                        }}
                    >
                        <svg
                            style={{ width: "24px", height: "24px", color: "#ef4444" }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: "20px", fontWeight: "bold", color: "white", marginBottom: "8px" }}>
                        Delete Account
                    </h2>
                    <p style={{ color: "#ef4444", fontSize: "14px", fontWeight: 600, margin: 0 }}>
                        This action cannot be undone. If you don't have your seed phrase saved, all funds will be unrecoverable.
                    </p>
                </div>

                <div style={{ marginBottom: "16px" }}>
                    <label style={labelStyle}>Enter wallet password to confirm</label>
                    <input
                        className="input-dark"
                        type="password"
                        style={{ ...inputStyle, width: "100%" }}
                        placeholder="Wallet password"
                        value={deletePassword}
                        onChange={(e) => {
                            setDeletePassword(e.target.value);
                            setDeleteError(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && deletePassword) {
                                handleDelete();
                            }
                        }}
                    />
                    {deleteError && (
                        <div style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
                            {deleteError}
                        </div>
                    )}
                </div>

                <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                    <button
                        onClick={handleClose}
                        style={{
                            padding: "10px 20px",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.1)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            color: "white",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn-danger"
                        onClick={handleDelete}
                        disabled={deleteLoading || !deletePassword}
                        style={{
                            padding: "10px 20px",
                            fontSize: "14px",
                            opacity: deleteLoading || !deletePassword ? 0.5 : 1,
                        }}
                    >
                        {deleteLoading ? "Deleting..." : "Delete Account"}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Password Prompt Modal ─────────────────────────────────────────── */

interface PasswordPromptModalProps {
    promptFor: {
        accountId: string;
        action: "transfer" | "privateKey" | "delete" | "whitelist";
        addressIndex?: number;
    } | null;
    onClose: () => void;
    onVerify: (password: string) => Promise<void>;
}

export const PasswordPromptModal: React.FC<PasswordPromptModalProps> = ({
    promptFor,
    onClose,
    onVerify,
}) => {
    const [passwordInput, setPasswordInput] = useState("");
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordLoading, setPasswordLoading] = useState(false);

    if (!promptFor) return null;

    const handleVerify = async () => {
        if (!passwordInput) return;
        setPasswordLoading(true);
        setPasswordError(null);
        try {
            await onVerify(passwordInput);
            setPasswordInput("");
            setPasswordError(null);
        } catch (err: unknown) {
            setPasswordError(getErrorMessage(err, "Incorrect password"));
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleClose = () => {
        setPasswordInput("");
        setPasswordError(null);
        onClose();
    };

    return (
        <div
            style={modalOverlay}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    handleClose();
                }
            }}
        >
            <div
                className="glass-card"
                style={{
                    width: "100%",
                    maxWidth: "420px",
                    padding: "24px",
                }}
            >
                <h2 style={{ fontSize: "18px", fontWeight: "bold", color: "white", marginBottom: "16px" }}>
                    Enter Wallet Password
                </h2>
                <p style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "16px" }}>
                    Password required for{" "}
                    {promptFor.action === "privateKey"
                        ? "viewing private keys"
                        : promptFor.action === "transfer"
                        ? "this transfer"
                        : promptFor.action === "whitelist"
                        ? "managing whitelist"
                        : "this action"}
                    .
                </p>

                <input
                    className="input-dark"
                    type="password"
                    style={{ ...inputStyle, width: "100%", marginBottom: "12px" }}
                    placeholder="Wallet password"
                    value={passwordInput}
                    onChange={(e) => {
                        setPasswordInput(e.target.value);
                        setPasswordError(null);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && passwordInput) {
                            handleVerify();
                        }
                    }}
                    autoFocus
                />

                {passwordError && (
                    <div
                        style={{
                            color: "#ef4444",
                            fontSize: "13px",
                            marginBottom: "12px",
                            padding: "8px 12px",
                            background: "rgba(239,68,68,0.1)",
                            borderRadius: "8px",
                        }}
                    >
                        {passwordError}
                    </div>
                )}

                <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                    <button
                        onClick={handleClose}
                        style={{
                            padding: "10px 20px",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.1)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            color: "white",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleVerify}
                        disabled={passwordLoading || !passwordInput}
                        style={{
                            padding: "10px 20px",
                            fontSize: "14px",
                            opacity: passwordLoading || !passwordInput ? 0.5 : 1,
                        }}
                    >
                        {passwordLoading ? "Verifying..." : "Unlock"}
                    </button>
                </div>
            </div>
        </div>
    );
};
